import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { showWarning } from '@/lib/utils/notifications'
import type {
  MailDetail,
  MailLogsParams,
  MailLogsResponse,
} from '../types'
import {
  compactMailLogsParams,
  normalizeMailLogsResponse,
} from '../utils/mailtrapApiHelpers'

export async function getMailLogs(
  params: MailLogsParams = {},
): Promise<MailLogsResponse> {
  try {
    const query = compactMailLogsParams(params)
    const response = await apiClient.get<unknown>('/api/mail_logs', {
      params: query,
    })
    return normalizeMailLogsResponse(response.data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load mail logs')
  }
}

export async function getMailDetail(
  mailId: number,
  type?: 'database',
): Promise<MailDetail> {
  try {
    const params = type ? { type } : {}
    const response = await apiClient.get<MailDetail>(`/api/mail_logs/${mailId}`, { params })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load mail detail')
  }
}

export async function resendMailLogMail(
  mailId: number,
  to?: string,
  type?: 'database',
): Promise<string> {
  try {
    const payload: Record<string, string> = {}
    if (to && to.trim()) payload.to = to.trim()
    if (type) payload.type = type
    const response = await apiClient.post<string | { message?: string }>(
      `/api/mail_logs/${mailId}`,
      payload,
    )
    if (typeof response.data === 'string') return response.data
    return response.data?.message || 'Mail sent successfully'
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to send mail')
  }
}

export async function getAllTemplateIds(): Promise<Record<string, string>> {
  try {
    const response = await apiClient.get<Record<string, string>>('/api/all_template_id')
    return response.data ?? {}
  } catch (error) {
    showWarning(extractErrorMessage(error) || 'Failed to load template IDs.')
    return {}
  }
}
